import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { UsersThree, ClipboardText } from "@phosphor-icons/react";
import { Section, Tile, Pill, fmtD } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

const STATE = {
  in: ["In", TONE_CLASSES.ok], done: ["Done for today", TONE_CLASSES.none], off: ["On leave", TONE_CLASSES.soon], sick: ["Off sick", TONE_CLASSES.overdue], none: ["Not clocked in", TONE_CLASSES.none],
};

/** My HR: a line manager's "My team" — who's in today, who's off, and the reviews they hold. Hidden for people with no reports. */
export function MyTeamCard() {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/hr/team/mine").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d?.is_manager) return null;
  const c = d.counts || {};
  return (
    <Section title="My team" icon={UsersThree} testid="my-team-card" action={<span className="text-xs text-muted-foreground">{fmtD(d.today)}</span>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="In today" value={c.in_today ?? 0} hint={`of ${d.reports.length}`} testid="my-team-in" />
          <Tile label="Off today" value={c.off_today ?? 0} hint="leave or sick" testid="my-team-off" />
          <Tile label="Leave to approve" value={c.pending_leave ?? 0} hint="waiting for you" testid="my-team-pending" />
          <Tile label="Reviews due" value={c.reviews_due ?? 0} hint="within 7 days" testid="my-team-reviews-due" />
        </div>
        {c.sickness_patterns > 0 && <p className="text-xs text-amber-700" data-testid="my-team-bradford-note">{c.sickness_patterns} team member{c.sickness_patterns !== 1 ? "s have" : " has"} a repeat short-sickness pattern — HR has the same flag and may be in touch.</p>}
        {d.reports.length > 0 && (
          <ul className="divide-y divide-border text-sm" data-testid="my-team-list">{d.reports.map((r) => (
            <li key={r.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`my-team-${r.id}`}>
              <div className="min-w-0">
                <p className="font-medium">{r.name}{r.job_title ? <span className="text-muted-foreground font-normal"> · {r.job_title}</span> : ""}</p>
                <p className="text-xs text-muted-foreground">{r.detail}{!r.on_payroll ? " · not linked to payroll" : ""}</p>
                {r.upcoming.length > 0 && <p className="text-[11px] text-muted-foreground">Upcoming leave: {r.upcoming.map((u) => `${fmtD(u.start)}${u.end !== u.start ? ` → ${fmtD(u.end)}` : ""}`).join(", ")}</p>}
              </div>
              <div className="flex items-center gap-1.5">
                {r.bradford && <Pill tone={r.bradford.band === "action" ? TONE_CLASSES.overdue : TONE_CLASSES.soon} testid={`my-team-${r.id}-bradford`}>Bradford {r.bradford.score} · {r.bradford.band === "action" ? "formal review" : "watch"}</Pill>}
                <Pill tone={STATE[r.state]?.[1] || TONE_CLASSES.none} testid={`my-team-${r.id}-state`}>{STATE[r.state]?.[0] || r.state}</Pill>
              </div>
            </li>
          ))}</ul>
        )}
        {d.reviews.length > 0 && (
          <div data-testid="my-team-reviews">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><ClipboardText size={12} /> Reviews you hold</p>
            <ul className="space-y-1 text-sm">{d.reviews.map((rv) => (
              <li key={rv.id} className="flex items-center justify-between gap-2">
                <Link to={`/hr/people/${rv.employee_id}?tab=reviews`} className="underline-offset-2 hover:underline" data-testid={`my-team-review-${rv.id}`}>{rv.kind_label} · {rv.employee_name}</Link>
                <Pill tone={rv.days < 0 ? TONE_CLASSES.overdue : rv.days <= 7 ? TONE_CLASSES.soon : TONE_CLASSES.none}>{rv.days < 0 ? `${-rv.days} d overdue` : rv.days === 0 ? "Today" : `${fmtD(rv.due_on)}`}</Pill>
              </li>
            ))}</ul>
          </div>
        )}
      </div>
    </Section>
  );
}
